/* CUSTOM SIZE — type a size, see the price, add it to the cart.
 *
 * ── THE COMMON CASE COSTS NOTHING ────────────────────────────────────────────
 * About 35% of typed sizes round onto a size we already sell (measured over all
 * 122,950 valid integer-cm sizes: 34.9%; 43.2% at ordinary area-rug
 * proportions). Those are resolved FROM THE PICKER'S OWN OPTIONS - the variant
 * id is already in the page - so they cost no request, no mint and no wait, and
 * the customer is told plainly that it is a standard size. Only a genuinely new
 * size reaches the Worker.
 *
 * ── WHY A REAL VARIANT HAS TO BE MINTED FOR THE REST ────────────────────────
 * A cart line on a Basic plan takes its price from its VARIANT. Measured on the
 * live store: POST /cart/add.js carrying `price: 100` came back `price: 7023`,
 * the variant's own, while accepting the custom-size property beside it. Cart
 * Transform would override it and needs a Shopify Function, which is Plus-only
 * for custom apps. So the Worker mints one. Measured end to end: 1.33 s to
 * mint, 0.86 s until it is addable, ~2.2 s from press to cart line.
 *
 * ── THE PRICE ON SCREEN IS DISPLAY; THE WORKER'S IS AUTHORITATIVE ───────────
 * Both read the SAME table, generated by `build_custom_prices.py` from
 * `pricing.price_for` - the function that prices the catalogue. The Worker
 * bundles its own copy and ignores whatever the client sends, so a tampered
 * page cannot buy a rug cheaply; the client's copy exists only so the number
 * can appear as the customer types.
 *
 * DO NOT PRICE FROM `custom_orders.price_for`. That flat `area x $15 x 4` is
 * the pre-2026-08-27 model and is stale by up to 63% at the small end - it
 * would have sold a custom 60x120 at $43.20 beside the identical pool size at
 * $70.23 in the same dropdown.
 */
(function () {
  'use strict';
  if (window.RugCustomSize) return;

  var CM_PER_IN = 2.54;

  /* WHAT THE CUSTOMER HAS DONE SO FAR, HELD OUTSIDE THE DOM.
   * Horizon replaces the product-information section wholesale on a variant
   * change, so anything stashed on an element dies with it. This is a single
   * page-view's worth of state: which segment is open, which unit, and the two
   * numbers. It is deliberately NOT persisted anywhere - it describes this
   * visit to this product page and nothing more. */
  var SEEN = { tab: 'standard', unit: 'in', w: '', l: '' };
  var PRICES = null, LOADING = null;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function money(n) {
    return (window.RUG_CUSTOM_SIZE && RUG_CUSTOM_SIZE.currency || '$')
      + Number(n).toFixed(2);
  }
  function inches(cm) { return Math.round(cm / CM_PER_IN); }
  /* PLAIN INCHES, NOT FEET-INCHES, AND ONLY FOR THE LIMITS LINE. `ftIn` is the
   * catalogue's own label format and must not change — `sizeLabel` uses it to
   * match the picker's option value, which is what makes the 35% bind case
   * fire. A limit reads better as 118" than as 9'10".
   *
   * A MINIMUM ROUNDS UP AND A MAXIMUM ROUNDS DOWN, so every number printed is
   * a number that actually validates. Rounding to nearest broke exactly one
   * boundary and it was the worst one: 100 cm is 39.37", the line said "length
   * from 39\"", and 39" is 99.06 cm — under the floor. We printed a limit and
   * then refused it. 40" is the smallest inch that passes; 24", 118" and 240"
   * were already inside their limits and do not move. */
  function inchMin(cm) { return Math.ceil(cm / CM_PER_IN) + '"'; }
  function inchMax(cm) { return Math.floor(cm / CM_PER_IN) + '"'; }
  function ftIn(cm) { var t = inches(cm); return Math.floor(t / 12) + "'" + (t % 12) + '"'; }

  /* The catalogue's own label format, two spaces before the bracket. A
   * different spelling here would not match the picker's option value and the
   * 35% bind case would silently never fire. */
  function sizeLabel(w, l) { return ftIn(w) + ' x ' + ftIn(l) + '  (' + w + 'x' + l + ' cm)'; }

  /* MIRRORS custom_orders.normalise / .tier. The short side is the width - the
   * pool is entirely portrait (0 of 335 entries are wider than long) and the
   * width is what has to fit across the loom. */
  function normalise(w, l) { w = Math.round(w); l = Math.round(l); return w <= l ? [w, l] : [l, w]; }
  /* THE INCH TOLERANCE. A size typed within `snap_tolerance_cm` ABOVE a size we
   * make is treated as that size instead of being tiered past it: 79" is the
   * nearest inch to 200 cm, rounds to 201, and used to tier to 210 - $14.27 more
   * for the same rug. The tolerance is 1 cm and it is MEASURED: converting a size
   * we make to the nearest whole inch and back overshoots by at most +1 cm, on 7
   * of 26 width rungs and 14 of 52 length steps.
   *
   * IT CONSULTS THE PRICE, AND THAT IS NOT BELT AND BRACES. Freight is not
   * proportional to area, so a SMALLER rug is sometimes dearer - 241x591 snapped
   * from 250x600 at $834.98 to 240x590 at $882.28. Over all 103,051 typed sizes
   * a naive snap raises the price on 179 of them (120 even width-only). With the
   * guard: 19,802 change, none costs more, median saving $15.47.
   *
   * THE TABLE IS THE ONE THE PRICE COMES FROM, so the size this returns and the
   * price shown for it cannot disagree. An older table without the key snaps
   * nothing, which is the previous behaviour rather than a crash. */
  /* THE LENGTH ROUNDS ONTO THE SHIPPED LADDER, NOT A BARE 10 cm STEP. There is
   * no maximum length any more, and the price is a LOOKUP - so the rungs the
   * table was generated at are the only lengths that can be priced. Rounding
   * to 10 cm above 10 m would produce a size the table has no entry for and
   * the customer would see "we could not price that" for a perfectly ordinary
   * rug. `PRICES.length_ladder` is generated by the same run as the prices, so
   * the two cannot disagree. */
  function upRung(l) {
    var L = PRICES.length_ladder;
    for (var i = 0; i < L.length; i++) { if (L[i] >= l) return L[i]; }
    return null;                       /* past the ladder — the caller reports it */
  }
  function downRung(l) {
    var L = PRICES.length_ladder, out = null;
    for (var i = 0; i < L.length; i++) { if (L[i] <= l) out = L[i]; else break; }
    return out;
  }

  /* A ROUND AND A SQUARE TIER AS ONE NUMBER. Their side is a width twice, so
   * tiering the two apart makes them unequal and the rug stops being the shape
   * that was chosen — an 80 cm round would have its length rounded onto the
   * LENGTH ladder, whose floor is 100, and arrive as an 80x100 oblong. Mirrors
   * custom_orders.tier(..., shape=). */
  function tierSide(side) {
    var ladder = PRICES.width_ladder;
    for (var i = 0; i < ladder.length; i++) if (ladder[i] >= side) return ladder[i];
    return ladder[ladder.length - 1];
  }

  function tier(w, l, shape) {
    if (shape === 'round' || shape === 'square') {
      var t = tierSide(Math.max(w, l));
      return [t, t];
    }
    var ladder = PRICES.width_ladder;
    var tw = ladder[ladder.length - 1];
    for (var i = 0; i < ladder.length; i++) { if (ladder[i] >= w) { tw = ladder[i]; break; } }
    var tl = upRung(l);
    if (tl === null) return [tw, null];
    var snap = PRICES.snap_tolerance_cm || 0;
    if (!snap) return [tw, tl];
    var sw = null;
    for (var j = 0; j < ladder.length; j++) {
      if (ladder[j] <= w && w <= ladder[j] + snap) { sw = ladder[j]; break; }
    }
    var base = downRung(l);
    var sl = (base !== null && base >= PRICES.min_length_cm && base <= l && l <= base + snap) ? base : null;
    var cw = sw === null ? tw : sw, cl = sl === null ? tl : sl;
    if (cw === tw && cl === tl) return [tw, tl];
    var here = PRICES.prices[tw + 'x' + tl], there = PRICES.prices[cw + 'x' + cl];
    if (here && there && there[0] <= here[0]) return [cw, cl];
    return [tw, tl];
  }

  /* ── THE ENVELOPE IS PER SHAPE, AND THE RULE IS GENERATED ────────────────
   * `rzValidate` comes from `rug-custom-size-rules.js`, written by
   * `custom_size_rules.py --write-js` from config/custom_size_limits.json. The
   * Worker carries the identical block, so the answer a customer sees as they
   * type and the answer the authority gives cannot disagree.
   *
   * WHAT WAS HERE UNTIL 2026-09-19: one envelope for every shape, and any two
   * equal sides refused as "We do not make square rugs — the two sides must
   * differ." A 200 cm ROUND is two equal sides, so choosing Round and typing
   * 200x200 was answered with a sentence about squares. Both halves were wrong
   * by then; the file above records what replaced them and where each number
   * comes from. */
  function validate(shape, w, l) {
    if (!isFinite(w) || w <= 0) return null;               /* not an error yet */
    if (l !== null && (!isFinite(l) || l <= 0)) return null;
    if (typeof rzValidate !== 'function') {
      /* §12: the rules file did not load. That is our fault and must not read
       * as a verdict about the size — refuse the SEGMENT, not the customer. */
      return 'Custom sizing is unavailable for a moment. Please reload.';
    }
    return rzValidate(shape, w, l);
  }

  function loadPrices() {
    if (PRICES) return Promise.resolve(PRICES);
    if (LOADING) return LOADING;
    var url = (window.RUG_CUSTOM_SIZE || {}).prices_url;
    if (!url) return Promise.reject(new Error('not_configured'));
    LOADING = fetch(url, { cache: 'force-cache' }).then(function (r) {
      if (!r.ok) throw new Error('prices_' + r.status);
      return r.json();
    }).then(function (d) { PRICES = d; return d; });
    return LOADING;
  }

  /* ── THE PICKER IS THE SOURCE OF TRUTH FOR WHAT WE ALREADY SELL ────────────
   * Custom variants are filtered OUT of it (rug-variant-picker RUG-FORK 5), so
   * an option being present means exactly "this is a standard pool size for the
   * shape currently selected" - which is the question being asked. Reading the
   * page beats a second copy of the pool: it cannot go stale and it already
   * carries the variant id. */
  function pickerOption(label) {
    var sel = $('select[name="options[Size]"]');
    if (!sel) return null;
    for (var i = 0; i < sel.options.length; i++) {
      var o = sel.options[i];
      if (o.textContent.trim().indexOf(label) === 0 || o.value.trim() === label) return o;
    }
    return null;
  }
  /* EVERY SHAPE THE LADDER DECLARES, NOT A HARDCODED PAIR. It read
   * `t === 'rectangle' || t === 'runner'` until 2026-09-19 and returned null
   * for the other three, so a customer on Round was treated as having chosen
   * nothing at all. The list comes from the generated rules, so adding a shape
   * is an edit to the data file. */
  function currentShape() {
    var known = (typeof RZ_SIZE_LIMITS === 'object' && RZ_SIZE_LIMITS)
      ? RZ_SIZE_LIMITS.shapes : { rectangle: 1, runner: 1 };
    var els = document.querySelectorAll('variant-picker input[type="radio"]:checked, '
                                        + 'variant-picker [aria-checked="true"]');
    for (var i = 0; i < els.length; i++) {
      var t = (els[i].value || els[i].textContent || '').trim().toLowerCase();
      if (known[t]) return t;
    }
    return null;
  }

  /* WHAT THE FORM ASKS FOR, FOR THE SHAPE ON SCREEN. A round is one number and
   * a square is one number; asking for two states a measurement the rug does
   * not have, and inviting a customer to type two different ones invites the
   * refusal rather than the rug. */
  function shapeInputs(shape) {
    var spec = (typeof RZ_SIZE_LIMITS === 'object' && RZ_SIZE_LIMITS
                && RZ_SIZE_LIMITS.shapes[shape]) || null;
    return spec ? spec.inputs : ['width', 'length'];
  }
  function singleField(shape) { return shapeInputs(shape).length === 1; }

  /* ── STATE ON SCREEN ─────────────────────────────────────────────────────── */
  function compute(box) {
    var unit = box._unit || 'cm';
    var shape = box._shape || currentShape() || 'rectangle';
    var one = singleField(shape);
    var wRaw = parseFloat($('[data-cs-w]', box).value);
    var lRaw = one ? wRaw : parseFloat($('[data-cs-l]', box).value);
    var w = unit === 'in' ? wRaw * CM_PER_IN : wRaw;
    var l = unit === 'in' ? lRaw * CM_PER_IN : lRaw;
    /* A ROUND OR A SQUARE IS COMPLETE WITH ONE NUMBER. Treating the hidden
     * second field as missing left the panel permanently `empty`, which is how
     * a form that asks for one number reports that nothing was typed. */
    var out = { ok: false, error: null, shape: shape,
                empty: !(isFinite(w) && w > 0 && isFinite(l) && l > 0) };
    if (out.empty) return out;
    out.error = validate(shape, w, one ? null : l);
    if (out.error) return out;
    var n = normalise(w, l), t = tier(n[0], n[1], shape);
    if (t[1] === null) {
      /* UNREACHABLE NOW THAT THE CEILING IS BACK — `validate()` refuses past
       * `max_length_cm` and the table is generated to exactly that ceiling, so
       * there is no length that validates and has no rung. It is kept as a
       * floor rather than deleted: if the two ever drift, this says so instead
       * of pricing from `undefined`.
       *
       * IT NO LONGER OFFERS A BESPOKE QUOTE. That sentence was written when
       * length was uncapped and it now contradicts the ceiling — and it
       * promised a service on a live storefront that nothing here delivers. */
      out.error = 'We could not price that size. Please tell us and we will fix it.';
      return out;
    }
    out.tw = t[0]; out.tl = t[1];
    out.label = sizeLabel(t[0], t[1]);
    out.key = t[0] + 'x' + t[1];
    var p = PRICES.prices[out.key];
    if (!p) { out.error = 'We could not price that size. Please tell us and we will fix it.'; return out; }
    out.price = p[0]; out.compare = p[1];
    out.rounded = (t[0] !== n[0] || t[1] !== n[1]);
    out.option = pickerOption(out.label);
    out.standard = Boolean(out.option);
    out.ok = true;
    return out;
  }

  /* ONE LIMIT UNDER EACH FIELD, IN BOTH UNITS, ALWAYS VISIBLE.
   * It was one combined sentence under both fields, which could not say which
   * number bound which field - a customer over the length had to work out
   * which half of "Width 24"-118" - length from 40"" was about them.
   *
   * BOTH UNITS ALWAYS, rather than following the toggle. The toggle decides
   * what you TYPE IN; the limit is a fact about the rug, and somebody working
   * in inches still benefits from seeing that 300 cm is the loom. It also
   * means the line never changes length when the unit changes, so the row
   * cannot reflow under the customer as they switch. */
  function limitLine(minCm, maxCm) {
    return inchMin(minCm) + '\u2013' + inchMax(maxCm)
         + ' \u00b7 ' + minCm + '\u2013' + maxCm + ' cm';
  }

  /* ── THE FORM FOLLOWS THE SHAPE ──────────────────────────────────────────
   * One field or two, the right words on them, and each one's own range under
   * it. It read `PRICES.min_width_cm` for every shape until 2026-09-19, which
   * printed the rectangle's 60-300 under a Round that starts at 80. */
  function shapeLimit(shape, field, bound) {
    var spec = (typeof RZ_SIZE_LIMITS === 'object' && RZ_SIZE_LIMITS
                && RZ_SIZE_LIMITS.shapes[shape]) || null;
    if (spec) return spec[field + '_cm'][bound];
    return PRICES ? PRICES[bound === 'min' ? 'min_' + field + '_cm'
                                           : 'max_' + field + '_cm'] : null;
  }

  function paintLimits(box) {
    var shape = box._shape || currentShape() || 'rectangle';
    var one = singleField(shape);
    var wLim = $('[data-cs-w-lim]', box), lLim = $('[data-cs-l-lim]', box);
    var wLab = $('[data-cs-w]', box), lLab = $('[data-cs-l]', box);
    var wField = wLab && wLab.closest('.rz-cs__field');
    var lField = lLab && lLab.closest('.rz-cs__field');
    var wName = $('.rz-cs__lab', wField || box), lName = $('.rz-cs__lab', lField || box);

    var first = shapeInputs(shape)[0];
    if (wName) wName.textContent = first.charAt(0).toUpperCase() + first.slice(1);
    if (wLim) wLim.textContent = limitLine(shapeLimit(shape, 'width', 'min'),
                                           shapeLimit(shape, 'width', 'max'));
    /* HIDDEN, NOT REMOVED. `[data-cs-l]` is read by `compute`, by the unit
     * toggle and by the resume path; removing it would make all three branch
     * on the shape, and a field that is hidden keeps its value for the
     * customer who switches back to Rectangle. */
    if (lField) lField.hidden = one;
    if (!one) {
      if (lName) lName.textContent = 'Length';
      if (lLim) lLim.textContent = limitLine(shapeLimit(shape, 'length', 'min'),
                                             shapeLimit(shape, 'length', 'max'));
    }
    if (box.classList) box.classList.toggle('rz-cs--one-field', one);
  }

  /* THE PLACEHOLDER FOLLOWS THE UNIT. "e.g. 200" under a field reading inches
   * is an instruction to type a rug five metres wide. */
  function placeholders(box, unit) {
    var w = $('[data-cs-w]', box), l = $('[data-cs-l]', box);
    var shape = box._shape || currentShape() || 'rectangle';
    /* A ONE-FIELD SHAPE GETS A SIZE THAT IS ACTUALLY IN ITS RANGE. "e.g. 79"
     * under a Round whose floor is 80 cm is an example that gets refused. */
    if (w) w.placeholder = singleField(shape)
      ? (unit === 'cm' ? 'e.g. 200' : 'e.g. 79')
      : (unit === 'cm' ? 'e.g. 200' : 'e.g. 79');
    if (l) l.placeholder = unit === 'cm' ? 'e.g. 300' : 'e.g. 118';
  }

  /* ── THE PRICE IS THE PAGE'S OWN PRICE BLOCK ──────────────────────────────
   * Not a second set of rules that matches today's styling and drifts from it
   * tomorrow - the SAME `<product-price>` element, cloned, with three numbers
   * rewritten. Colours, sizes, the struck-through original, the "N% off" chip
   * and LIMITED TIME OFFER are then identical by construction, and a change to
   * `snippets/price.liquid` or `rug-sale-styles.liquid` reaches both at once.
   *
   * IT SKIPS THE STICKY BAR'S COPY. There are two on a product page and the
   * sticky one is styled for a dark bar; taking the first would put bar
   * styling in the form. */
  function mainPrice() {
    var all = document.querySelectorAll('product-price');
    for (var i = 0; i < all.length; i++) {
      var p = all[i];
      if (p.closest('[data-cs-ui]')) continue;
      if (p.closest('sticky-add-to-cart') || p.closest('.sticky-add-to-cart')) continue;
      if (p.closest('cart-drawer')) continue;
      return p;
    }
    return null;
  }

  /* The number sits as a TEXT NODE after a visually-hidden label ("Sale price"),
   * so the label is kept and only the trailing text is replaced. Rewriting
   * textContent would delete the label and take the screen-reader announcement
   * with it. */
  function setNum(span, text) {
    if (!span) return;
    var n = span.lastChild;
    while (n && n.nodeType !== 3) n = n.previousSibling;
    if (n) n.nodeValue = ' ' + text;
    else span.appendChild(document.createTextNode(' ' + text));
  }

  function fillPrice(box, sale, compare) {
    var mount = $('[data-cs-price-mount]', box);
    if (!mount) return;
    if (!mount.firstChild) {
      var src = mainPrice();
      if (!src) {
        /* NO PRICE BLOCK TO CLONE. This cannot happen on the only template
         * that renders this form — `product.rug.json` carries the price block
         * in its buy column — but returning here would leave the customer
         * looking at a size and an Add button with NO PRICE, which is the one
         * outcome worse than unstyled. So it degrades to plain text.
         *
         * IT IS NOT A SECOND STYLING IMPLEMENTATION, which is what the "one
         * definition" rule forbids: it carries no classes and claims no
         * treatment. It is a legible sentence in place of a missing element. */
        mount.textContent = money(sale) + (compare > sale ? '  (was ' + money(compare) + ')' : '');
        return;
      }
      var c = src.cloneNode(true);
      c.removeAttribute('data-block-id');
      c.removeAttribute('data-product-id');
      /* THE SOURCE IS HIDDEN AT THE MOMENT WE CLONE IT, AND THE CLONE MUST NOT
       * INHERIT THAT. `choose()` hides the page's own price the instant Custom
       * is picked, and cloning copies the `hidden` attribute — so the copy
       * arrived carrying it and the stylesheet's own
       * `product-price[hidden]{display:none!important}` duly hid the copy too.
       * Measured on the live page: the mount held 757 bytes of correct markup
       * reading "50% off / Sale price $423.84 / Regular price $847.68 /
       * Limited time offer" at `display: none`.
       *
       * IT IS NOT A STYLE OVERRIDE. Visibility is the one property the two
       * elements are SUPPOSED to disagree about: the page's price describes the
       * selected variant and is hidden precisely because it no longer applies,
       * while this one describes the size being typed. Everything the "one
       * definition" rule is about — the colours, the sizes, the discount
       * treatment, LIMITED TIME OFFER — is still the theme's own and untouched.
       *
       * AND IT IS WHY A STYLE COMPARISON IS NOT A VERIFICATION. Computed
       * colour and font-size resolve perfectly on a `display: none` element, so
       * an earlier check reported the two prices byte-identical on 11
       * properties while this one was not on screen at all. Ask whether it
       * RENDERS before asking whether it matches. */
      c.hidden = false;
      mount.appendChild(c);
    }
    var el = mount.firstChild;
    var on = compare > sale;
    var pct = on ? Math.round((1 - sale / compare) * 100) : 0;
    var off = el.querySelector('.rz-off');
    if (off) { off.textContent = pct + '% off'; off.hidden = !on; }
    setNum(el.querySelector('.price'), money(sale));
    var cmp = el.querySelector('.compare-at-price');
    setNum(cmp, money(compare));
    if (cmp) cmp.hidden = !on;
    var lto = el.querySelector('.rz-limited');
    if (lto) lto.hidden = !on;
  }

  function paint(box) {
    var s = compute(box);
    box._state = s;
    var err = $('[data-cs-error]', box), sum = $('[data-cs-summary]', box);
    var wHint = $('[data-cs-w-alt]', box), lHint = $('[data-cs-l-alt]', box);
    var unit = box._unit || 'cm';

    /* THE OTHER UNIT IS ALWAYS SHOWN, under the field it belongs to, so
     * somebody working in feet never has to convert 300 cm in their head. */
    function alt(node, raw) {
      if (!node) return;
      var v = parseFloat(raw);
      if (!isFinite(v) || v <= 0) { node.textContent = ''; return; }
      node.textContent = unit === 'cm'
        ? '= ' + ftIn(v) : '= ' + Math.round(v * CM_PER_IN) + ' cm';
    }
    /* `[data-cs-w-max]` and `widthMaxText()` were read here until 2026-09-08.
     * The element no longer exists - each field carries its own limit now - and
     * `widthMaxText` was never defined, so this line survived only because the
     * guard short-circuited on a missing node. A dangling call behind a null
     * check is a throw waiting for the element to come back. */
    paintLimits(box);
    alt(wHint, $('[data-cs-w]', box).value);
    alt(lHint, $('[data-cs-l]', box).value);

    err.textContent = s.error || '';
    err.hidden = !s.error;
    sum.hidden = !s.ok;
    syncMainAdd(box);
    if (!s.ok) return;

    fillPrice(box, s.price, s.compare);

    /* THE NOTE NO LONGER NAMES THE WOVEN SIZE. By direct decision the tier is
     * internal: they type a size, they see a price. It is not concealed from
     * the PURCHASE - `commit()` writes the woven size onto the cart line as the
     * "Custom size" property, so it is on the basket, the confirmation and the
     * order, which is where a size that differs from the typed one has to be
     * legible. What is gone is a number on the form that raised a question
     * about our pricing rather than answering one about their rug. */
    var note = $('[data-cs-note]', sum);
    if (note) {
      note.textContent = s.standard
        ? 'This is one of our standard sizes — it goes straight in your basket.'
        : 'Woven to order for you.';
    }
    var eta = $('[data-cs-eta]', sum);
    if (eta) eta.textContent = shipsLine(s.tw);
  }

  /* THE SHIPPING LINE, NEVER A DELIVERY DATE (the owner, 2026-10-15, constitution §8):
   * "Made to order · ships within 7 days" up to 260 cm wide, "… 20 days" wider
   * (FD-0547). The width is the short side, `s.tw`, as everywhere in this file.
   * The two lines and the break come from the block's settings, so this file
   * holds no number of its own. Until 2026-10-15 it quoted an arrival day. */
  function shipsLine(widthCm) {
    var cfg = window.RUG_CUSTOM_SIZE || {};
    var over = Number(cfg.wide_over_cm) || 260;
    return Number(widthCm) > over
      ? (cfg.ships_wide || 'Made to order · ships within 20 days')
      : (cfg.ships || 'Made to order · ships within 7 days');
  }

  /* ── TURNSTILE, lazily, on the first commit ───────────────────────────────── */
  var TS_LOAD_MS = 12000;
  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (window.__rzTsLoad) return window.__rzTsLoad;
    window.__rzTsLoad = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true; s.defer = true;
      s.onload = function () { resolve(window.turnstile); };
      s.onerror = function () { reject(new Error('challenge_script')); };
      document.head.appendChild(s);
      setTimeout(function () { if (!window.turnstile) reject(new Error('challenge_timeout')); },
                 TS_LOAD_MS);
    });
    return window.__rzTsLoad;
  }
  function challengeToken(box) {
    var cfg = window.RUG_CUSTOM_SIZE || {};
    if (!cfg.sitekey) return Promise.resolve(null);
    return loadTurnstile().then(function (ts) {
      return new Promise(function (resolve, reject) {
        var host = $('[data-cs-turnstile]', box);
        if (!host) { reject(new Error('challenge_unavailable')); return; }
        var done = false;
        var timer = setTimeout(function () {
          if (!done) { done = true; reject(new Error('challenge_timeout')); }
        }, TS_LOAD_MS);
        try {
          /* RESET BEFORE EACH EXECUTE — a token is single-use and expires in
           * 300 s, and a stale one is refused by the Worker and would read to
           * the customer as a rate limit. */
          if (host._wid != null) { ts.reset(host._wid); }
          else {
            host._wid = ts.render(host, {
              sitekey: cfg.sitekey, appearance: 'interaction-only', execution: 'execute',
              callback: function (t) { if (!done) { done = true; clearTimeout(timer); resolve(t); } },
              'error-callback': function (code) {
                if (done) return; done = true; clearTimeout(timer);
                if (window.console && console.warn) {
                  console.warn('[rug-custom-size] Turnstile error ' + (code || '(no code)')
                               + ' on ' + location.hostname);
                }
                reject(new Error('challenge_' + (code || 'unavailable')));
              }
            });
          }
          ts.execute(host._wid);
        } catch (e) { if (!done) { done = true; clearTimeout(timer); reject(e); } }
      });
    });
  }

  /* ── THE MINT IS PRE-WARMED, AND THE REASON IS MEASURED ──────────────────
   * A newly minted variant is not immediately addable to the cart. The Admin
   * API returns as soon as it holds the variant; the STOREFRONT is behind it,
   * and by a VARIABLE amount - measured on this store: addable 0.86 s after
   * the mint on one occasion, and still `422 Cart Error - Cannot find variant`
   * many seconds later on another. So "mint when they press Add" fails
   * unpredictably, and it fails at the worst possible moment: after we have
   * already made the rug.
   *
   * SO THE MINT STARTS WHEN THE SIZE IS SETTLED, NOT WHEN THE BUTTON IS
   * PRESSED. Once a valid custom size has been on screen for `WARM_MS` - long
   * enough that it is a size and not a keystroke - the variant is created in
   * the background. The customer then reads the price, the note and the
   * delivery date, which is several seconds of propagation we get for free,
   * and the press adds a variant that already exists.
   *
   * WHAT IT COSTS, STATED PLAINLY: a variant is minted for somebody who may
   * never buy. That is the trade for a button that works, and it is why the
   * reaper exists (`custom_variant_reaper.py`) - it deletes unsold custom
   * variants, so the cost of a pre-warm nobody bought is one row in a nightly
   * report rather than permanent.
   *
   * IT IS NOT SPECULATIVE ON A POOL SIZE. About 35% of typed sizes round onto
   * a size we already sell; those never reach the Worker at all. */
  var WARM_MS = 1200;

  function warm(box) {
    var s = box._state;
    if (!s || !s.ok || s.standard) return;             /* pool sizes need nothing */
    var key = s.key;
    if (box._warmKey === key && box._warm) return;     /* already in flight for this size */
    box._warmKey = key;
    var cfg = window.RUG_CUSTOM_SIZE || {};
    if (!cfg.endpoint) return;
    box._warm = challengeToken(box).then(function (token) {
      return fetch(cfg.endpoint, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        /* THE SHAPE TRAVELS WITH THE SIZE. The Worker cannot tell a 200 cm
         * round from a 200 cm square by looking at the numbers, and it is
         * the authority — so what the customer chose is sent, not left to
         * be re-derived at the other end. */
        body: JSON.stringify({ handle: cfg.handle, w_cm: s.tw, l_cm: s.tl,
                               shape: s.shape,
                               sid: sessionId(), token: token })
      }).then(function (r) { return r.json().then(function (d) { d._status = r.status; return d; }); });
    });
    /* A pre-warm failure is SILENT. Nothing has been asked for yet, so there is
     * nothing to report; the press re-tries and reports for itself. */
    box._warm.catch(function () {});
  }

  /* THE MINT OUTRUNS THE STOREFRONT, AND THE GAP IS SHORT AND REAL.
   * `productVariantsBulkCreate` returns as soon as the Admin API has the
   * variant; `/cart/add.js` is served by the storefront, which is a moment
   * behind. Measured: a standalone probe found the variant addable 0.86 s
   * after the mint returned and on its FIRST attempt - but the first live run
   * of this form, which adds the instant the Worker answers, got a refusal.
   * The variant existed the whole time (verified in the Admin API) and the
   * customer would have been told "we could not add that to your basket"
   * about a rug we had just successfully made for them.
   *
   * SO IT RETRIES, BRIEFLY AND ONLY HERE. This is not a general cart retry -
   * an ordinary add either works or is a real failure. It is the one place
   * where "not found yet" is an expected, self-clearing state, so it is the
   * one place that waits for it. Bounded at ~3 s: past that it is not
   * propagation delay, it is a fault, and saying so beats spinning. */
  function addWhenVisible(variantId, props) {
    var tries = 0;
    function go() {
      return window.RugCart.add(variantId, 1, 'custom-size', props)
        .catch(function (e) {
          /* ~9 s of backstop. The pre-warm does most of the waiting; this
           * covers the case where the customer presses Add immediately. */
          if (++tries >= 15) throw e;
          return new Promise(function (r) { setTimeout(r, 600); }).then(go);
        });
    }
    return go();
  }

  /* ── THE PAGE HAS ONE ADD TO CART, AND THIS IS HOW CUSTOM REACHES IT ──────
   * By direct decision (2026-09-08) the custom form has no Add of its own.
   * Two buttons doing one job made the customer choose between them, and put
   * the custom path somewhere the standard path is not.
   *
   * IT IS INTERCEPTED ON THE BUTTON'S CLICK, IN CAPTURE, AND THAT IS MEASURED
   * RATHER THAN ASSUMED. A capture listener on the FORM's `submit` fires and
   * changes nothing — Horizon still added the line (verified: intercepted
   * true, cart 6 items). It does not submit the form; it acts on the button.
   * A capture listener on the BUTTON stops it dead (verified: cart 0).
   *
   * DELEGATED AT THE DOCUMENT, for the reason the segment control already is:
   * Horizon replaces the product-information section wholesale, so a listener
   * bound to the button at load time is attached to a node that is no longer
   * in the page.
   *
   * THE STICKY BAR COUNTS TOO. It is a second control for the same action, and
   * leaving it on the standard path would let a customer with a custom size
   * scrolled past the picker buy the SELECTED VARIANT instead — silently, at a
   * different size and a different price. */
  var ADD_SELECTOR = 'button[name="add"], .sticky-add-to-cart__button, .add-to-cart-button';

  function mainAdd() {
    return document.querySelector('product-form-component button[name="add"]')
        || document.querySelector('button[name="add"]');
  }

  /* WHAT THE BUTTON SAYS WHILE CUSTOM IS OPEN. It is the one control for two
   * different purchases, so it has to say which one it is about to make -
   * otherwise the customer reads "Add to cart" and reasonably expects the size
   * showing in the dropdown they can no longer see. */
  /* THE LABEL IS REWRITTEN ON THE FORM'S BUTTON AND THE DISABLED STATE ON EVERY
   * INTERCEPTED ONE, AND THE ASYMMETRY IS MEASURED RATHER THAN LAZY.
   * `.sticky-add-to-cart__button` has NO element children - its content is bare
   * text nodes reading "Add to cart (1)", where the number is Horizon's own
   * quantity. With no `.button-text` to write into, a relabel would have to
   * replace the button's whole textContent and put a REMEMBERED count back on
   * the way out, which is a stale number on a live storefront the moment the
   * quantity changes while the custom segment is open. Disabling is a state, so
   * it clobbers nothing - and it is the half that matters, because a control
   * that stays pressable with an unusable size is the one a customer scrolled
   * past the picker will reach for. */
  function syncMainAdd(box) {
    var main = mainAdd();
    syncOneAdd(box, main, true);
    var btns = document.querySelectorAll(ADD_SELECTOR);
    for (var i = 0; i < btns.length; i++) {
      if (btns[i] !== main) syncOneAdd(box, btns[i], false);
    }
  }

  /* THE LABEL IS A TEXT NODE, NOT THE BUTTON. Horizon's add-to-cart button has
   * no `.button-text` wrapper: the words sit in a bare text node BESIDE the
   * checkmark-burst markup and, since 2026-09-19, beside the price span that
   * `rug-atc-price.js` writes into. Assigning `textContent` to the BUTTON
   * therefore replaced every child with one string — measured on bbb134-aspen,
   * where the button read a flattened "Add to cart" with no price and no burst
   * while a manufacturer page, which never loads this file, kept both. Writing
   * into the text node relabels exactly what a label is and leaves the element
   * children where the rest of the page put them. */
  function labelOf(btn) {
    var el = btn.querySelector('.button-text');
    if (el) return { el: el };
    var nodes = [];
    for (var i = 0; i < btn.childNodes.length; i++) {
      var n = btn.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue.trim()) nodes.push(n);
    }
    if (!nodes.length) {
      var t = document.createTextNode('');
      btn.insertBefore(t, btn.firstChild);
      nodes.push(t);
    }
    return { nodes: nodes };
  }

  function labelRead(l) {
    if (l.el) return l.el.textContent;
    var out = '';
    for (var i = 0; i < l.nodes.length; i++) out += l.nodes[i].nodeValue;
    return out;
  }

  function labelWrite(l, v) {
    if (l.el) { l.el.textContent = v; return; }
    l.nodes[0].nodeValue = v;
    for (var i = 1; i < l.nodes.length; i++) l.nodes[i].nodeValue = '';
  }

  function syncOneAdd(box, btn, relabel) {
    if (!btn) return;
    var label = labelOf(btn);
    var custom = box && box._tab === 'custom';
    var s = box && box._state;
    if (relabel) {
      if (btn._csOriginal === undefined) btn._csOriginal = labelRead(label);
      labelWrite(label, custom
        ? ((window.RUG_CUSTOM_SIZE || {}).add_label || 'Add my size to basket')
        : btn._csOriginal);
    }
    /* DISABLED ONLY WHILE CUSTOM IS OPEN AND THE SIZE IS NOT YET USABLE. On the
     * standard segment the button is Horizon's business and is left alone -
     * disabling it there would break the ordinary purchase for a form the
     * customer is not even looking at. */
    if (custom) btn.disabled = !(s && s.ok);
    else if (btn._csDisabled) btn.disabled = false;
    btn._csDisabled = custom && !(s && s.ok);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest(ADD_SELECTOR);
    if (!btn) return;
    var box = document.querySelector('[data-cs-ui]');
    if (!box || box._tab !== 'custom') return;      /* standard: Horizon's job */
    e.preventDefault();
    e.stopImmediatePropagation();
    /* ONE PRESS OF THE STICKY BAR IS TWO CLICK EVENTS, MEASURED. Horizon's
     * sticky bar does not add anything itself - it PROGRAMMATICALLY CLICKS the
     * main button (confirmed by hooking HTMLElement.prototype.click: one press
     * logged a synthetic click on `add`). So this handler legitimately fires
     * twice for one gesture, on two different buttons, and stopping propagation
     * cannot prevent that: the forwarding happens in a listener registered
     * before ours. Measured before the latch: 2 x POST /cart/add.js, both ours,
     * quantity 2 in the cart from one press.
     *
     * THE LATCH IS "A COMMIT IS ALREADY RUNNING", NOT A TIME WINDOW. It is set
     * synchronously inside commit() before any request, so the second event -
     * which arrives in the same tick - finds it set. A window would be a guess
     * about how fast the forwarding is.
     *
     * DROPPING `.sticky-add-to-cart__button` FROM THE SELECTOR WOULD ALSO FIX
     * THE COUNT AND IS THE WORSE FIX: it would leave the sticky bar correct
     * only for as long as Horizon keeps forwarding, and the day it stopped, a
     * customer with a typed size would buy the SELECTED VARIANT silently. */
    if (box._committing) return;
    var s = box._state;
    if (!s || !s.ok) { paint(box); return; }        /* say why, add nothing */
    commit(box, btn);
  }, true);

  /* ── COMMIT ──────────────────────────────────────────────────────────────── */
  function commit(box, btn) {
    var s = box._state;
    if (!s || !s.ok) return;
    /* THE BUTTON IS PASSED IN, because it is no longer ours. The custom form
     * has no Add of its own: the page has ONE Add to cart and this drives it.
     * `mainAdd()` is the fallback for a caller that did not name one. */
    btn = btn || mainAdd();
    if (!btn) return;
    /* SET BEFORE ANYTHING ASYNC, so the second event of one gesture finds it.
     * Cleared by done() and fail(), which are the only two exits past here. */
    box._committing = true;
    var label = btn.querySelector('.button-text, [data-cs-add-label]') || btn;
    var err = $('[data-cs-error]', box);
    var original = label.textContent;

    function fail(msg) {
      err.textContent = msg; err.hidden = false;
      btn.disabled = false; label.textContent = original;
      clearTimeout(box._slowTimer);
      box._committing = false;
    }

    /* THERE IS NO MODAL TO CLOSE NOW, so "done" is the control returning to a
     * usable state and nothing else. The confirmation is the CART DRAWER,
     * which opens on add for every surface in this theme; a second success
     * message inside the form would be two announcements of one event, and the
     * form stays open so a second size can be typed without reopening
     * anything. */
    function done() {
      clearTimeout(box._slowTimer);
      btn.disabled = false;
      label.textContent = original;
      box._committing = false;
    }

    /* ── THE STANDARD-SIZE PATH: no request, no mint, no wait ─────────────── */
    if (s.standard) {
      var vid = s.option.getAttribute('data-variant-id');
      if (vid) {
        btn.disabled = true;
        label.textContent = 'Adding…';
        window.RugCart.add(Number(vid), 1, 'custom-size-standard')
          .then(function () { done(); })
          .catch(function () { fail('We could not add that to your basket. Please try again.'); });
        return;
      }
      /* The option is in the picker but carries no id — that is our bug, not
       * the customer's size, so it falls through to the Worker rather than
       * telling them something false about the rug (§12). */
    }

    btn.disabled = true;
    /* ~2.2 s MEASURED END TO END, so this is a button state and not a progress
     * bar. A bar with narrated steps for a two-second wait is theatre — the
     * competitor feature this project reverse-engineered advances six
     * hardcoded percentages on a timer and reports nothing. */
    label.textContent = 'Cutting your ' + s.tw + ' × ' + s.tl + ' cm rug…';
    err.hidden = true;
    /* PAST DOUBLE THE MEASURED TIME, say so. The watchdog is on SILENCE, never
     * a deadline: the mint may already have happened and giving up on it would
     * strand a variant while losing the cart line. */
    box._slowTimer = setTimeout(function () {
      label.textContent = 'Still working — this one is taking longer than usual';
    }, 4500);

    var cfg = window.RUG_CUSTOM_SIZE || {};
    /* USE THE PRE-WARMED MINT IF ONE IS IN FLIGHT FOR THIS EXACT SIZE. A second
     * request would be harmless - the Worker binds rather than minting twice,
     * because the SKU is derived from the size - but it would also throw away
     * the propagation head start this whole arrangement exists to buy. */
    var minted = (box._warmKey === s.key && box._warm)
      ? box._warm
      : challengeToken(box).then(function (token) {
          return fetch(cfg.endpoint, {
            method: 'POST', headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ handle: cfg.handle, w_cm: s.tw, l_cm: s.tl,
                                   shape: s.shape,
                                   sid: sessionId(), token: token })
          }).then(function (r) {
            return r.json().then(function (d) { d._status = r.status; return d; });
          });
        });
    minted.then(function (d) {
      if (!d.ok) throw d;
      /* WHAT THEY TYPED AND WHAT WE WEAVE ARE BOTH RECORDED. The visible
       * property is what the factory cuts; the typed pair is kept so a
       * question about the order can be answered from the order itself. */
      var typed = box._typed || '';
      return addWhenVisible(d.variant_id, {
        'Custom size': d.tiered[0] + ' × ' + d.tiered[1] + ' cm ('
                       + ftIn(d.tiered[0]) + ' × ' + ftIn(d.tiered[1]) + ')',
        'Made to order': 'Cut to your measurements',
        '_typed': typed,
        '_custom_sku': d.sku
      });
    }).then(function () {
      done();
    }).catch(function (e) {
      var code = (e && e.error) || String(e && e.message || e);
      /* EACH REFUSAL SAYS THE ONE THING THE CUSTOMER CAN ACT ON. A limit is
       * not an error and must not read as one — the budget or the clock says
       * nothing about their rug. */
      var msg = 'Something went wrong making your size. Please try again.';
      if (e && e.message) msg = e.message;
      if (code === 'rate' || code === 'session')
        msg = 'You have made a few custom sizes just now — please try again shortly, '
              + 'or pick a size from the list.';
      else if (code === 'product_cap')
        msg = 'We are making a lot of custom sizes of this rug today. Please try again tomorrow, '
              + 'or pick a size from the list.';
      else if (code === 'product_full')
        msg = 'This rug cannot take another custom size. Please pick one from the list.';
      else if (code === 'code_fault' || code === 'not_configured' || code === 'upstream')
        msg = 'We could not make that size just now — it is our system, not your measurements. '
              + 'Please try again in a few minutes.';
      else if (String(code).indexOf('challenge') === 0)
        msg = String(code) === 'challenge_script'
          ? 'The security check could not load. If you use a content blocker, allow '
            + 'challenges.cloudflare.com and try again.'
          : 'The security check did not pass. Please try again.';
      fail(msg);
    });
  }

  function sessionId() {
    try {
      var k = 'og_session_id', v = localStorage.getItem(k);
      if (!v) { v = String(Math.random()).slice(2) + String(Date.now()); localStorage.setItem(k, v); }
      return v;
    } catch (e) { return ''; }
  }

  /* ── WIRING ──────────────────────────────────────────────────────────────── */
  /* ── WHERE THE CONTROL GOES ────────────────────────────────────────────────
   * The block renders below the variant picker because that is where its
   * settings live; it BELONGS between the Size heading and the size list.
   * `rug-variant-picker.liquid` (RUG-FORK 6) renders an empty `[data-cs-slot]`
   * there, and the block's `<template>` is CLONED into it.
   *
   * IT IS A CLONE AND NOT A MOVE, AND THAT IS NOT TASTE — MOVING IT WAS TRIED
   * AND HORIZON DELETED IT. Measured 2026-09-08 with a MutationObserver:
   * changing the size runs a variant update that reconciles the picker against
   * freshly fetched server HTML, and a node present in the DOM but absent from
   * that HTML is removed as foreign. The observer logged `REMOVED DIV.rz-cs`,
   * the slot survived, and the control was gone until a reload.
   *
   * SO THE SOURCE NEVER MOVES AND THERE IS ALWAYS SOMETHING TO PUT BACK. The
   * clone has exactly the same fate on the next reconcile, which is what
   * `watch()` is for. Re-cloning resets the typed numbers, and that is correct
   * rather than merely acceptable: a variant change can change the SHAPE, and a
   * runner's envelope is not a rectangle's.
   *
   * NO SLOT IS NOT AN ERROR. The slot is only rendered for the LAST option, so
   * a picker that has not been forked, or one showing Size as buttons rather
   * than a dropdown, simply gets no custom control — the block itself renders
   * nothing visible, so nothing is left stranded on the page. */
  function slotOf(src) {
    return (src.closest('.shopify-section') || document).querySelector('[data-cs-slot]');
  }

  function fill(src) {
    var slot = slotOf(src);
    if (!slot) return null;
    var ui = slot.querySelector('[data-cs-ui]');
    if (ui) return ui;
    var tpl = src.querySelector('template[data-cs-tpl]');
    if (!tpl || !tpl.content) return null;
    slot.appendChild(tpl.content.cloneNode(true));
    return slot.querySelector('[data-cs-ui]');
  }

  /* THE WATCHER IS ON THE SECTION, NOT ON THE SLOT, because a reconcile
   * replaces the slot itself — an observer bound to the old node would be
   * watching something no longer in the document. It only ever acts when the
   * slot has no UI in it, so re-filling cannot loop: the append it performs
   * satisfies the very condition that triggered it. */
  function watch(src) {
    if (src._csWatch) return;
    var host = src.closest('.shopify-section');
    if (!host || !window.MutationObserver) return;
    src._csWatch = new MutationObserver(function () {
      var slot = slotOf(src);
      if (!slot || slot.querySelector('[data-cs-ui]')) return;
      var ui = fill(src);
      if (ui) init(ui);
    });
    src._csWatch.observe(host, { childList: true, subtree: true });
  }

  /* SWITCHING IS `hidden`, NEVER A REMOVAL, AND THAT IS WHAT PRESERVES THE
   * SELECTION. A `<select>` keeps its value while it is not displayed, so
   * coming back from custom shows exactly the size that was chosen before —
   * there is no state to save and restore, and therefore none to get wrong.
   *
   * THE CSS CARRIES `[hidden] { display: none !important }` FOR BOTH TARGETS.
   * `[hidden]` is a UA-stylesheet rule and loses to any class rule that sets
   * `display`, which is how a "hidden" panel stays on screen — this project has
   * already paid for that once, on the rug finder's status spinner. */
  function choose(box, which) {
    box._tab = which;
    var panel = $('[data-cs-panel]', box);
    if (panel) panel.hidden = which !== 'custom';
    if (box._std) box._std.hidden = which === 'custom';

    /* THE PRICE UNDER THE TITLE IS THE SELECTED VARIANT'S, AND ON CUSTOM THAT
     * IS NOT WHAT THEY ARE BUYING. Leaving it up puts two prices on the page
     * for one purchase, and the wrong one is the larger and the higher up. It
     * comes back on Standard, where it is true again.
     *
     * THE WHOLE BLOCK GOES, NOT JUST THE FIGURE: `<product-price>` also carries
     * the instalment offer and the tax note, and instalments quoted against a
     * price that is not being charged are the same error one line down. */
    var mp = mainPrice();
    if (mp) mp.hidden = which === 'custom';
    Array.prototype.forEach.call(box.querySelectorAll('[data-cs-tab]'), function (b) {
      b.setAttribute('aria-checked', String(b.getAttribute('data-cs-tab') === which));
    });
  }

  function init(box) {
    if (!box || box._csInit) return;
    box._csInit = true;
    /* INCHES LEAD. This constant is the default, and the order of the two unit
     * buttons in the markup only reflects it. */
    box._unit = 'in';
    box._std = (box.closest('.shopify-section') || document)
                 .querySelector('[data-cs-standard]');

    /* NO ENDPOINT, NO CUSTOM SEGMENT — the block's own schema says "Empty
     * disables the form", and until now that was only half true: `commit()`
     * returned early, so the Add button was present, enabled, and did NOTHING
     * when pressed. A silent dead button is worse than an error and far worse
     * than an absent control.
     *
     * WITH THE SEGMENT GONE THE PICKER IS EXACTLY WHAT IT IS TODAY, which is
     * what makes this a usable off switch: blanking the setting in the theme
     * editor removes the feature from the storefront without touching code,
     * and is the right lever while the Worker is not yet deployed. */
    var cfg = window.RUG_CUSTOM_SIZE || {};
    if (!cfg.endpoint) {
      var seg = $('[data-cs-seg]', box), pan = $('[data-cs-panel]', box);
      if (seg) seg.hidden = true;
      if (pan) pan.hidden = true;
      if (box._std) box._std.hidden = false;
      return;
    }

    /* ── THE FIX FOR "TYPING THROWS ME OUT" ──────────────────────────────
     * THE CUSTOM FIELDS SIT INSIDE HORIZON'S PRODUCT <form>, INSIDE
     * <variant-picker>. Measured on the live page, the ancestor chain from the
     * width field is:  input → label → div×5 → form → variant-picker.
     *
     * So a `change` from a size field bubbles into Horizon's variant machinery,
     * which reads it as "the customer picked a different variant" and refetches
     * `/products/<handle>?option_values=…`, then morphs the response in. The
     * morph DELETES the cloned custom UI, the watcher below re-clones it, and
     * `init()` runs again from the top — landing the customer back on Standard
     * with empty fields. Measured, one digit, one field:
     *
     *     t=605   change fires on the width field
     *     t=606   FETCH /products/bbb102-willow?option_values=…
     *     t=2016  REMOVED DIV.rz-cs          ← the form the customer was using
     *     after   fresh node, width "", tab "standard"
     *
     * `input` ALONE IS HARMLESS — isolated: 0 reconciles. It is `change` that
     * does it, which is why this only bites once a field is left (blur), or
     * immediately if the number spinner is used. That is the "after a second or
     * two" in the report: the gap is the fetch, not a timer.
     *
     * STOPPED AT THE UI ROOT, NOT PREVENTED. `stopPropagation` keeps the event
     * from reaching the form while leaving it fully live inside this control;
     * `preventDefault` would do nothing here, and cancelling the event for our
     * own handlers would break the unit conversion.
     *
     * IT IS SCOPED TO THIS SUBTREE, so a REAL variant change is untouched -
     * verified in the same session: with this listener installed, editing a
     * size field caused 0 reconciles while changing the Size select still
     * caused exactly 1. The picker still works. */
    box.addEventListener('change', function (e) { e.stopPropagation(); });

    /* ── AND THE TAB SURVIVES A LEGITIMATE RECONCILE ─────────────────────────
     * Fix 1 stops typing causing one, but a reconcile still happens for real
     * reasons - the customer changes Shape while the custom form is open. The
     * watcher then re-clones the UI and calls init(), which used to hard-code
     * `choose(box, 'standard')` and `_unit = 'in'`, throwing away a
     * half-entered size for no reason: the typed dimensions are still valid,
     * because the shape is derived from the numbers rather than from the
     * picker.
     *
     * THE STATE LIVES OUTSIDE THE DOM because the DOM is what gets replaced.
     * A value stashed on the element cannot survive the element being deleted,
     * which is the whole failure being fixed. */
    choose(box, SEEN.tab);
    syncMainAdd(box);
    box._unit = SEEN.unit;
    placeholders(box, SEEN.unit);
    Array.prototype.forEach.call(box.querySelectorAll('[data-cs-unit]'), function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-cs-unit') === SEEN.unit));
    });
    if (SEEN.w || SEEN.l) {
      var rw = $('[data-cs-w]', box), rl = $('[data-cs-l]', box);
      if (rw) rw.value = SEEN.w;
      if (rl) rl.value = SEEN.l;
      if (SEEN.tab === 'custom') {
        loadPrices().then(function () {
          paintLimits(box);
          paint(box);
        }).catch(function () {});
      }
    }

    Array.prototype.forEach.call(box.querySelectorAll('[data-cs-tab]'), function (b) {
      b.addEventListener('click', function () {
        var to = b.getAttribute('data-cs-tab');
        if (to === box._tab) return;
        choose(box, to);
        SEEN.tab = to;
        syncMainAdd(box);
        if (to !== 'custom') return;
        /* THE TABLE IS FETCHED ON THE FIRST LOOK AT THE FORM, not on page load
         * — the overwhelming majority of visitors never open it — and not at
         * the moment of pressing Add, where a network wait would sit between
         * the customer and their basket. */
        loadPrices().then(function () {
          paintLimits(box);
          paint(box);
          var f = $('[data-cs-w]', box); if (f) f.focus();
        }).catch(function () {
          var err = $('[data-cs-error]', box);
          if (err) {
            err.textContent = 'We could not load our sizes just now. Please try again.';
            err.hidden = false;
          }
        });
      });
    });

    ['[data-cs-w]', '[data-cs-l]'].forEach(function (sel) {
      var f = $(sel, box);
      if (f) f.addEventListener('input', function () {
        SEEN.w = ($('[data-cs-w]', box).value || '');
        SEEN.l = ($('[data-cs-l]', box).value || '');
        box._typed = SEEN.w + ' x ' + SEEN.l + ' ' + box._unit;
        if (PRICES) { paint(box); scheduleWarm(box); }
      });
    });

    var toggle = box.querySelectorAll('[data-cs-unit]');
    Array.prototype.forEach.call(toggle, function (b) {
      b.addEventListener('click', function () {
        var to = b.getAttribute('data-cs-unit');
        if (to === box._unit) return;
        /* THE TYPED NUMBERS ARE CONVERTED, NOT CLEARED. Switching unit after
         * typing 250 should show 98", not an empty field. */
        ['[data-cs-w]', '[data-cs-l]'].forEach(function (sel) {
          var f = $(sel, box), v = parseFloat(f.value);
          if (isFinite(v) && v > 0) {
            f.value = to === 'in' ? (v / CM_PER_IN).toFixed(1) : Math.round(v * CM_PER_IN);
          }
        });
        box._unit = to;
        /* The CONVERTED numbers are what is remembered, not the originals -
         * they are what the fields now hold, so a re-clone restores exactly
         * what is on screen rather than the same rug in the other unit. */
        SEEN.unit = to;
        SEEN.w = ($('[data-cs-w]', box).value || '');
        SEEN.l = ($('[data-cs-l]', box).value || '');
        Array.prototype.forEach.call(toggle, function (x) {
          x.setAttribute('aria-pressed', String(x.getAttribute('data-cs-unit') === to));
        });
        placeholders(box, to);
        paintLimits(box);
        if (PRICES) paint(box);
      });
    });

    /* NO ADD BINDING HERE. The page's own Add to cart is intercepted at the
     * document, below, so it keeps working across the section re-renders that
     * a per-element listener does not survive. */
  }

  function scheduleWarm(box) {
    clearTimeout(box._warmTimer);
    box._warmTimer = setTimeout(function () { warm(box); }, WARM_MS);
  }

  /* ── THE PANEL FOLLOWS THE SHAPE BUTTONS ─────────────────────────────────
   * Choosing a shape changes which fields the form asks for and which range
   * sits under them, and Horizon morphs the picker rather than reloading, so
   * nothing here is re-created. Delegated at the document for the same reason
   * every other handler in this file is: an element listener does not survive
   * the morph. Idempotent — it only repaints when the shape actually changed,
   * so it cannot fire on its own output. */
  function syncShape() {
    var box = document.querySelector('[data-cs-ui]');
    if (!box) return;
    var now = currentShape() || 'rectangle';
    if (box._shape === now) return;
    box._shape = now;
    paintLimits(box);
    placeholders(box, box._unit || 'in');
    if (PRICES) paint(box);
  }
  document.addEventListener('change', function () { setTimeout(syncShape, 60); }, true);
  document.addEventListener('click', function () { setTimeout(syncShape, 240); }, true);

  function boot() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-custom-size]'),
      function (src) {
        var ui = fill(src);
        if (ui) init(ui);
        watch(src);
      });
    syncShape();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);

  window.RugCustomSize = { init: init, fill: fill, tier: tier, normalise: normalise,
                           sizeLabel: sizeLabel, validate: validate,
                           ftIn: ftIn, deliveryPhrase: deliveryPhrase };
}());
